/**
 * Pure styling + value-formatting helpers for `<ModelGrid>` (in-platform-
 * model-view Phase 3 Session 3). No React, no DOM — fully unit-testable.
 *
 * Four responsibilities:
 *   1. `getCellClassName(cell, isSelected, opts)` — Tailwind class string per
 *      cell type (label / input / formula / output / empty), with optional
 *      error-cell + bracket overrides composed in.
 *   2. `isErrorValue(value)` — matches the 8 Excel/HyperFormula error
 *      literals (`#CYCLE!`, `#REF!`, `#VALUE!`, `#DIV/0!`, `#NAME?`,
 *      `#NULL!`, `#NUM!`, `#N/A`); used by both `formatCellValue` (preserve
 *      the literal) and `getCellClassName` (apply `text-danger` override).
 *   3. `formatCellValue(value, cell, schema)` — dispatch chain:
 *      cell.style.numberFormat → cell.namedRange dataType → Intl fallback.
 *   4. `getTableInputRegions(tableInputs, sheetName)` — pre-computes the
 *      bounding boxes of all table-input regions on a sheet. Memoize the
 *      result in `<ModelGrid>` via `useMemo` keyed on `(schema.tableInputs,
 *      layout.name)` so the per-cell `isInRegion` lookup stays O(N) where N
 *      is the small number of regions.
 */
import clsx from 'clsx';
import type { GridCell, TableInputDefinition } from '@arcanum-hq/shared-types';
import type { ModelViewResponse } from '@/lib/api';
import { formatCurrency, formatNumber, formatPercent } from '@/lib/format';
import { formatExcelNumber } from '@/lib/excel-format';
import { parseA1 } from './grid-helpers';

type ModelViewSchemaSubset = ModelViewResponse['schema'];

/**
 * The CellError object shape HyperFormula returns for failed evaluations.
 * Returned verbatim by `ComputationEngine.getValueAt` and threaded through
 * `getModelView` to the web grid (per in-platform-model-view Phase 2 contract).
 *
 * Shape source: `packages/agents/src/lib/engine/computation-engine.ts:344`
 * (the `getValueAt` method); HF v2.7+ `DetailedCellError` carries `value`
 * (the `#NUM!` literal), `type` (e.g. `'NUM'`), `address` (e.g.
 * `'MonthlyCashFlow!D77'`), and an optional `message`.
 */
export interface CellErrorObject {
  type: string;
  value: string;
  address?: string;
  message?: string;
}

/**
 * Type guard for CellError. Narrow check: must have both `type: string`
 * and `value: string` to avoid false-positive matches on user-supplied
 * objects with arbitrary `type`/`value` keys. Added by model-view-fidelity
 * Phase 1 (2026-04-26).
 */
export function isCellErrorObject(value: unknown): value is CellErrorObject {
  return (
    value !== null &&
    typeof value === 'object' &&
    'type' in value &&
    typeof (value as { type: unknown }).type === 'string' &&
    'value' in value &&
    typeof (value as { value: unknown }).value === 'string'
  );
}

/**
 * Extract the `address` field from a CellError, or `undefined` for any
 * other value. Consumed by `<ModelGrid>` for the cell `title` hover hint
 * so devs can read the source-of-error address at a glance. Added by
 * model-view-fidelity Phase 1 (2026-04-26).
 */
export function getCellErrorAddress(value: unknown): string | undefined {
  return isCellErrorObject(value) ? value.address : undefined;
}

const ERROR_LITERAL_RE =
  /^#(CYCLE|ERROR|REF|VALUE|DIV\/0|NAME\?|NULL|NUM|N\/A)!?$/;

/**
 * Excel + HyperFormula error literals. `#N/A` covers lookup misses (VLOOKUP /
 * INDEX/MATCH); the others cover formula evaluation failures. Optional `!`
 * suffix accepts both Excel's canonical `#REF!` form and bare `#REF` strings
 * some HF code paths emit. Accepts both string form and the CellError object
 * form HF returns from `getCellValue` (extended by model-view-fidelity Phase 1).
 *
 * `#ERROR!` was added to the regex in model-view-fidelity Phase 1 (2026-04-26)
 * after Phase 0's diagnostic surfaced 244 cells with `type: 'ERROR'` in
 * `multifamily-v1@1.0.1` that the prior 8-pattern regex was silently
 * mis-classifying as non-errors.
 */
export function isErrorValue(value: unknown): boolean {
  if (typeof value === 'string') return ERROR_LITERAL_RE.test(value);
  if (isCellErrorObject(value)) return ERROR_LITERAL_RE.test(value.value);
  return false;
}

/**
 * `#N/A` is semantically distinct from the other Excel/HF error literals: it
 * means "value not available" — usually a `VLOOKUP` / `INDEX/MATCH` miss
 * because a required input hasn't been provided yet (e.g. op-asset's
 * `INDEX(CashFlowNOITrailingAnnualized, MATCH("Stabilized", ...))` returns
 * `#N/A` until the user sets the timeline date inputs that populate the
 * cash flow's "Stabilized" label).
 *
 * The other error literals (`#REF!`, `#VALUE!`, `#NUM!`, `#CYCLE!`,
 * `#NAME?`, `#NULL!`, `#DIV/0!`, `#ERROR!`) signal real workbook bugs and
 * SHOULD render as red-text error literals via `isErrorValue` + the
 * `text-danger` className override.
 *
 * `#N/A` should render as an em dash in muted styling instead — visually
 * communicating "this will populate once you fill in the missing inputs"
 * rather than "the model is broken." Surfaced 2026-05-04 via
 * `operating-asset-e2e-validation` Session 2 walk on a partial-input
 * conversation; Pope confirmed the em-dash treatment.
 *
 * Accepts both string form (`'#N/A'`) and the CellError object form
 * (`{ type: 'NA', value: '#N/A', ... }`).
 */
export function isMissingValueError(value: unknown): boolean {
  if (typeof value === 'string') return value === '#N/A' || value === '#N/A!';
  if (isCellErrorObject(value)) {
    return (
      value.type === 'NA' || value.value === '#N/A' || value.value === '#N/A!'
    );
  }
  return false;
}

/**
 * Excel fill-color hex → Intelligence Terminal Tailwind token.
 *
 * Intent-driven mapping (NOT literal hex) — the hex strings here are the
 * canonical-workbook examples; close variants in the same family map to the
 * same token. Unmapped colors fall through to neutral (no fill) with a
 * one-time `console.warn` keyed on (hex, sheet, addr) so devs notice during
 * ingest verification and can extend the table.
 *
 * Mapping rationale (per design.md § Excel-Color → Design-Token Mapping):
 *  - Yellow → bg-accent-dim/20 (existing input-cell token; preserves on-brand
 *    aesthetic vs. raw `#FFFF00` glare against the dark Intelligence Terminal bg) @design-token bg-accent-dim/20
 *  - Dark blue → bg-blue-dim (section banners)
 *  - Light blue/grey → bg-panel-bg (sub-banners, headers)
 *  - Medium blue → bg-blue-dim/40 (total rows)
 *  - Subtotal blue → bg-blue-dim/25
 *
 * Hex strings are uppercase, no leading `#`. Lookup normalises both.
 * Added by model-view-fidelity Phase 2 (2026-04-26).
 */
export const EXCEL_FILL_TOKEN_MAP: Readonly<Record<string, string>> = {
  // Input cells — yellow family
  FFFF00: 'bg-accent-dim/20',
  FFEB9C: 'bg-accent-dim/20',
  FFD966: 'bg-accent-dim/20',
  // Pale yellow / cream input variants (added 2026-04-26 after Phase 2
  // walkthrough surfaced 80+ cells across DealSummary / DevBudget / UnitMix)
  FFFFC9: 'bg-accent-dim/20',
  FFFFD3: 'bg-accent-dim/20',
  // Pale highlight yellow used on MonthlyCashFlow rows 48 + 52 (added
  // 2026-05-26 after the idle-Builder console warned about unmapped fills).
  FFFF99: 'bg-accent-dim/20',
  // Section banners — dark blue family
  '1F4E78': 'bg-blue-dim',
  '305496': 'bg-blue-dim',
  '2F5496': 'bg-blue-dim',
  // Variant dark blue used for header rows on MCF / DevBudget / UnitMix /
  // AnnualCashFlow (added 2026-04-26 after Phase 2 walkthrough surfaced
  // ~30 cells)
  '1F497D': 'bg-blue-dim',
  // Sub-banners — light blue/grey family
  D9E1F2: 'bg-panel-bg',
  DDEBF7: 'bg-panel-bg',
  EDEDED: 'bg-panel-bg',
  // Light gray sub-banner used on MonthlyCashFlow header row
  // (added 2026-05-04 after operating-asset-e2e-validation Session 2 walk
  // surfaced 1 cell at MonthlyCashFlow!12-2)
  F2F2F2: 'bg-panel-bg',
  // Total rows — medium blue family
  B4C7E7: 'bg-blue-dim/40',
  '8FAADC': 'bg-blue-dim/40',
  // Subtotal rows
  BDD7EE: 'bg-blue-dim/25',
  // Light green accent family — used on op-asset value-cell rows + waterfall
  // summary rows (added 2026-05-04 after operating-asset-e2e-validation
  // Session 2 walk surfaced 13 cells in UnitMix + 4 cells in AnnualWaterfall)
  ECF4E8: 'bg-accent-dim/15',
  E2EFDA: 'bg-accent-dim/20',
};

// Non-undefined tier values (the schema enum minus `undefined`).
// Derived from GridCell['tier'] — single source of truth; if the schema
// enum changes, the Record<Tier,string> maps below become exhaustive-checked
// by the compiler (missing key = compile error).
type Tier = NonNullable<GridCell['tier']>;

// (1) Static literal Tailwind class map — consumed by getCellClassName (S6).
//     Tailwind v4 has NO safelist: `bg-band-${tier}` is silently never emitted.
//     Every class string is a COMPLETE literal (web CLAUDE.md F03; research/phase-2.md § 11 R5).
export const TIER_BAND_CLASS: Record<Tier, string> = {
  // F10 NEW (Phase 1 of model-view-excel-parity, ADR-0013):
  // super-section banner — distinct from description-header (which is grand-total color).
  // Tailwind v4 no-safelist: bg-band-supersection MUST be a complete static literal.
  'super-section': 'bg-band-supersection font-bold text-text-bright',
  'section-opener': 'bg-band-section', // reserved — consumed by Phase-4 brick rendering
  // 2026-05-26 — repointed from heavy bg-band-description (Tier-1 dark) to
  // lighter bg-band-subheader. description-header now stamps panel-internal
  // sub-section header rows (Project Uses / Project Sources etc.) via the
  // structural post-pass in layout-extractor; the heavy tier-1 treatment was
  // never consumed in production.
  'description-header': 'bg-band-subheader font-bold text-text-bright',
  // F2 (Phase 1, ADR-0013): swap outline → border-t + border-b at full color
  // (no alpha fade). Excel subtotals are bold + single top rule + single bottom rule.
  subtotal: 'border-t border-b border-band-subtotal-outline font-bold',
  // F3 (Phase 1, ADR-0013): add border-t-2 double-rule top; alpha bump
  // handled via --color-band-total token (mvld-6 reconcile + 25-30% alpha
  // bump applied at TIER_BAND_HEX + @theme; bg-band-total class unchanged).
  total:
    'border-t-2 border-band-subtotal-outline bg-band-total font-bold text-text-bright',
  'grand-total': 'bg-band-description font-bold text-text-bright',
};

// (2) #rrggbb companion-map for any band needing an INLINE style (e.g. a
//     box-shadow / SVG / arbitrary value a `bg-band-*` class can't propagate).
//     Mirrors SIGNAL_DOT_HEX (HeroMetricCell.tsx:19 — Record<Enum,string> of
//     #rrggbb LITERALS, NOT var(--x); var(--color-x)NN is invalid CSS).
//     Each hex is the SAME Lovable-sourced value as its --color-band-* token
//     (Session 4); kept in sync by construction. Same-line @design-token tag
//     so the S4-extended raw-hex audit (ADR 0006) exempts these as the
//     deliberate per-state lookup-map pattern the script's header names.
export const TIER_BAND_HEX: Record<Tier, string> = {
  // F10 NEW (Phase 1 of model-view-excel-parity, ADR-0013):
  // Color choice: institutional navy — see --color-band-supersection in index.css @theme.
  // Must match --color-band-supersection token in index.css @theme.
  'super-section': '#1A3A5F', // @design-token resolves --color-band-supersection
  'section-opener': '#7A8580', // @design-token resolves --color-band-section
  // 2026-05-26 — repointed to --color-band-subheader (lighter Lovable
  // sub-header tone). description-header used to mirror grand-total (dark
  // near-black) but that mapping was never consumed in production.
  'description-header': '#7A858517', // @design-token resolves --color-band-subheader
  subtotal: '#7A858538', // @design-token resolves --color-band-subtotal
  // F3 (Phase 1, ADR-0013): alpha bump 9% → 30% for stronger total wash.
  // Sequence: mvld-6 G-channel reconcile (old→new) applied FIRST, then alpha bump.
  // Final value matches --color-band-total in index.css (G-byte 80, alpha 4D ≈ 30%).
  total: '#7A85804D', // @design-token resolves --color-band-total
  // Inc-5: mirrors --color-band-description (dark near-black, same as description-header).
  'grand-total': '#2A302E', // @design-token resolves --color-band-description
};

/** Module-level dedup set so we warn once per (hex, sheet, addr) triple
 *  instead of per render. Reset between test files via vitest's
 *  per-file module isolation; within-file resets via the exported
 *  `__resetUnmappedColorWarnSetForTests` hook below. */
const unmappedColorWarnSet = new Set<string>();

/**
 * Map an Excel fill-color hex to its Intelligence Terminal token, OR `null`
 * if no mapping exists. Caller renders the cell with no fill on null return.
 * Unmapped colors trigger a one-time `console.warn` keyed on
 * `(hex, sheet, addr)` — devs see one warn per unmapped color per cell
 * address (not per render).
 *
 * Added by model-view-fidelity Phase 2 (2026-04-26).
 */
export function mapFillColorToToken(
  hex: string,
  sheet: string,
  addr: string
): string | null {
  const normalized = hex.replace(/^#/, '').toUpperCase();
  const token = EXCEL_FILL_TOKEN_MAP[normalized];
  if (token) return token;
  const key = `${normalized}|${sheet}|${addr}`;
  if (!unmappedColorWarnSet.has(key)) {
    unmappedColorWarnSet.add(key);
    // eslint-disable-next-line no-console
    console.warn(
      `[model-view] unmapped Excel fill color "${normalized}" at ${sheet}!${addr}; rendering with no fill. Add to EXCEL_FILL_TOKEN_MAP in cell-style.ts.`
    );
  }
  return null;
}

/** Test-only reset for the warn-dedup set. Vitest doesn't reset module state
 *  between tests in the same file; this hook lets test suites that need
 *  predictable warn behavior clear the set in `afterEach`. NOT for production
 *  use. Added by model-view-fidelity Phase 2 (2026-04-26). */
export function __resetUnmappedColorWarnSetForTests(): void {
  unmappedColorWarnSet.clear();
}

/**
 * Return the Tailwind class string for a cell, given its type + selection +
 * optional overrides.
 *
 * Composition order matters — error-styling and the table-input bracket
 * override the cell-type's text/background tokens, so they're applied LAST.
 * Selection ring (outline) is independent of foreground colors and is
 * applied via `outline outline-2 outline-accent outline-offset-[-2px]` so
 * the ring doesn't shrink the cell's content area.
 *
 * The `case 'empty':` branch is defensive — `<ModelGrid>` short-circuits
 * empty cells with `return null` BEFORE rendering, so this branch is
 * unreachable from the grid's hot path today. It stays for two reasons:
 * (1) the function's contract is "given any GridCell, return its className"
 * — narrowing the union to non-empty would leak the renderer's optimization
 * into the helper's API; (2) Phase 4's `<CellDetailPanel>` will preview cell
 * styling (including empty cells) when the user clicks a non-data cell, and
 * removing the branch would force the panel to special-case empty cells.
 */
export function getCellClassName(
  cell: GridCell,
  isSelected: boolean,
  opts: {
    isError?: boolean;
    /** `#N/A` lookup miss — render in muted color with em-dash glyph.
     *  Distinct from `isError` which signals a real workbook bug. When
     *  `isMissing` is set, `isError` is ignored (em dash never wears
     *  the danger token). Added 2026-05-04. */
    isMissing?: boolean;
    bracketLeft?: boolean;
    pulsing?: boolean;
    /** Sheet name — required when `cell.style.fillColor` is set so the
     *  unmapped-color warn key has context. Optional otherwise.
     *  Added by model-view-fidelity Phase 2 (2026-04-26). */
    sheet?: string;
    /** Excel-parity (2026-05-25): negative numbers in accounting format
     *  (parens) render in red — mirrors workbook's `#,##0;[Red](#,##0)`
     *  convention. Caller derives via `value < 0 && fragment.body.startsWith('(')`.
     *  Falls through cellType=label / empty unchanged. */
    isNegativeAccounting?: boolean;
  } = {}
): string {
  // Inc-6: BG-axis cascade fix (mirrors Inc-3.1 on the text axis).
  // Tailwind v4 emits .bg-band-* rules BEFORE .bg-bg in the compiled stylesheet
  // (band tokens were added later → lower rule-order position). Same-specificity
  // cascade means the LATER-declared .bg-bg always wins, overpainting the band.
  // Fix: single-source the BG decision — gate bg-bg OUT of base for fill-bearing
  // tiers so their TIER_BAND_CLASS bg-* is the ONLY bg-* declaration on the cell.
  // Untiered cells + subtotal (outline-only, no bg) keep bg-bg as before.
  // FILL_BEARING_TIERS must stay in sync with TIER_BAND_CLASS entries that carry
  // a bg-* token (currently: super-section, section-opener, description-header, total, grand-total).
  const FILL_BEARING_TIERS = new Set<Tier>([
    'super-section', // F10 NEW — carries bg-band-supersection class
    'section-opener',
    'description-header',
    'total',
    'grand-total',
  ]);
  const wantsFillTier = cell.tier != null && FILL_BEARING_TIERS.has(cell.tier);
  const base = `flex items-center px-2 text-[11px] leading-[22px] truncate cursor-pointer${
    wantsFillTier ? '' : ' bg-bg'
  }`;
  const selectionRing = isSelected
    ? 'outline outline-2 outline-accent outline-offset-[-2px] z-[1]'
    : '';
  const cellTypeClasses = (() => {
    switch (cell.cellType) {
      case 'label':
        return clsx(
          'font-sans text-text-bright',
          cell.style?.bold && 'font-bold',
          cell.style?.italic && 'italic',
          // 2026-05-26: Excel underline → text-decoration. Surfaces the
          // Capital Stack Summary column-header row (`$ Amount` / `$ / SF`
          // at row 7) as visually distinct from regular labels. Pair with
          // font-bold-on-underline so it reads as a header (Excel renders
          // underlined cells at slightly heavier weight via its font hinting;
          // we approximate with semibold).
          cell.style?.underline && 'underline font-semibold'
        );
      case 'input':
        return 'font-mono tabular-nums text-blue bg-input-fill-amber border-l border-l-blue/40 text-right';
      case 'formula':
      case 'output':
        return 'font-mono tabular-nums text-right';
      case 'empty':
        return 'bg-transparent border-transparent cursor-default';
    }
  })();
  const tierClasses = cell.tier
    ? // Inc-5: subtotal now reads from TIER_BAND_CLASS (outline-band-subtotal-outline,
      // mid-grey per Lovable DevBudgetBrief.tsx:131). Was hardcoded outline-panel-border-hi.
      // All tiers unified through the single TIER_BAND_CLASS lookup.
      TIER_BAND_CLASS[cell.tier] // subtotal: outline+bold; total/grand-total: fill+bold; section-opener/description-header: reserved (Phase-4)
    : '';
  // Single-source text color for formula/output cells (Inc-3.1 fidelity fix
  // superseding Inc-3 clsx reorder). Tailwind v4 cascade is stylesheet-rule-
  // order-driven, not classList-order; the prior Inc-3 reorder set both
  // `text-text` (from cellTypeClasses) and `text-accent` (from referencesClasses)
  // on xref cells, and stylesheet order picked `text-text` regardless of classList
  // order, hiding xref-green. Single-source eliminates the competition.
  // See remediation-plan §3 + reviewer F-Inc3 finding.
  //
  // Gate: formula/output cells get their ONLY text-color token here.
  //   xref → text-accent (brand green)
  //   local/undefined → text-text (neutral off-white, back-compat)
  // Input cells: referencesClasses returns '' or 'text-accent' per the old rule
  // (inputs have references: undefined in practice — classifyReferences is gated
  // on formula — so this always resolves to '' for inputs, preserving text-blue).
  // Label/empty: same empty-string pass-through (no competing text-* added).
  const referencesClasses =
    cell.cellType === 'formula' || cell.cellType === 'output'
      ? cell.references === 'cross-sheet'
        ? 'text-accent'
        : 'text-text'
      : cell.references === 'cross-sheet'
      ? 'text-accent'
      : '';
  // Phase 2 (model-view-fidelity, 2026-04-26): consume the rest of
  // gridCellStyleSchema. Borders, fill, and horizontal alignment are
  // applied AFTER cell-type defaults so explicit workbook styling wins
  // on conflicting Tailwind properties (Tailwind cascade: later class
  // beats earlier on same property).
  // F-D1 (post-ship remediation, 2026-05-25): TIER_BAND_CLASS owns top/bottom
  // borders on subtotal (border-t + border-b @ band-subtotal-outline mid-grey)
  // and total (border-t-2 @ band-subtotal-outline). When the cell's workbook
  // style ALSO emits borderTop/borderBottom, the panel-border (#0F1520 near-  // @design-token resolves --color-panel-border
  // black on near-black bg-bg) silently overpaints the tier's mid-grey rule —
  // Tailwind v4 cascade lets the LATER stylesheet rule win at same specificity.
  // Gate panel-border emission so the tier-class wins on tiers that own those
  // sides. Left/right always emit (no tier conflict).
  const tierOwnsTopBorder = cell.tier === 'subtotal' || cell.tier === 'total';
  const tierOwnsBottomBorder = cell.tier === 'subtotal';
  const borderClasses = clsx(
    cell.style?.borderTop &&
      !tierOwnsTopBorder &&
      'border-t border-t-panel-border',
    cell.style?.borderBottom &&
      !tierOwnsBottomBorder &&
      'border-b-panel-border',
    cell.style?.borderLeft && 'border-l border-l-panel-border',
    cell.style?.borderRight && 'border-r-panel-border'
  );
  // Phase-4 Increment-3 fidelity correction (defect b): input arm at :309-310
  // already owns the fill (`bg-input-fill-amber`); suppress the legacy
  // EXCEL_FILL_TOKEN_MAP yellow→accent-dim mapping for input cells so we
  // don't double-paint two competing backgrounds. ADR 0010: amber-on-blue is
  // the canonical input chrome; `cellType==='input'` is the generic
  // discriminator — zero per-model branch.
  const fillClass =
    cell.cellType === 'input'
      ? ''
      : cell.style?.fillColor
      ? mapFillColorToToken(
          cell.style.fillColor,
          opts.sheet ?? 'unknown',
          `${cell.row}-${cell.col}`
        ) ?? ''
      : '';
  const alignClass =
    cell.style?.horizontalAlign === 'left'
      ? 'text-left'
      : cell.style?.horizontalAlign === 'center'
      ? 'text-center'
      : cell.style?.horizontalAlign === 'right'
      ? 'text-right'
      : '';

  // Missing-value (em-dash) styling — applied INSTEAD of error styling so
  // the muted-text token wins. Mutually exclusive with `errorOverride` via
  // the explicit `!opts.isMissing` guard on the error branch below.
  // (2026-05-04 — Pope confirmed em-dash treatment for #N/A lookup misses.)
  const missingOverride = opts.isMissing
    ? 'text-text-muted font-mono tabular-nums'
    : '';
  // Error styling overrides cell-type colors but preserves the cell-type
  // alignment (right for input/formula/output, left for label). Apply last
  // so the danger token wins on `text-*` collisions. Skip when `isMissing`
  // is set — `#N/A` is a missing-input affordance, not a workbook bug.
  const errorOverride =
    opts.isError && !opts.isMissing ? 'text-danger font-mono tabular-nums' : '';
  const bracketOverride = opts.bracketLeft
    ? 'border-l-2 border-l-accent/30'
    : '';
  // Tree-navigator-driven scroll-to-cell pulse (Phase 4 Session 1). Uses
  // inset box-shadow rather than outline so the 2s flash stacks cleanly with
  // the selection ring (outline-based) without re-flowing layout. The
  // `.animate-pulse-cell` keyframe is defined in `apps/web/src/index.css`.
  const pulseOverride = opts.pulsing ? 'animate-pulse-cell z-[1]' : '';
  // Excel-parity (2026-05-25): negative-in-parens render in red, matching
  // workbook's `#,##0;[Red](#,##0)` convention. Applies to formula/output
  // cells only — input/label/empty pass through unchanged. Applied AFTER
  // error/missing/tier overrides so the red wins on text-* collisions for
  // standard negative values; suppressed when isError/isMissing/tier
  // overrides are active (those signal a different state).
  const negativeOverride =
    opts.isNegativeAccounting &&
    !opts.isError &&
    !opts.isMissing &&
    (cell.cellType === 'formula' || cell.cellType === 'output')
      ? 'text-danger'
      : '';
  // Phase-4 Increment-3 fidelity correction: `referencesClasses` moved BEFORE
  // `tierClasses` so total/grand-total `text-text-bright` wins over xref
  // `text-accent` on the `text-*` collision (R1 §3 truth table — total rows are
  // bold WHITE regardless of kind). Supersedes the Phase-3 C7 adjacency rule
  // for this precise case; xref-green on non-total cells unchanged (subtotal/
  // untiered tier doesn't set `text-*`). See remediation-plan §3.
  return clsx(
    base,
    cellTypeClasses,
    referencesClasses, // Phase 3 — moved BEFORE tierClasses (Increment-3 reorder; was after)
    tierClasses, // ← model-view-lovable-density-alignment P2 S6; wins text-* over referencesClasses on total/grand-total
    borderClasses,
    fillClass,
    alignClass,
    selectionRing,
    missingOverride,
    errorOverride,
    bracketOverride,
    pulseOverride,
    negativeOverride
  );
}

/**
 * Lookup tableInput definitions in the schema and convert each one's
 * `topLeftCell` / `bottomRightCell` (A1 strings) into 1-based row/col bounds,
 * filtering by sheet. Returns the bounding boxes the grid uses to apply the
 * left-edge bracket indicator on the leftmost col of each region.
 *
 * Memoize the result in `<ModelGrid>` via `useMemo(() => getTableInputRegions(
 * schema.tableInputs, layout.name), [schema.tableInputs, layout.name])` so the
 * per-cell `cellInRegion` check is O(N) over a typically-tiny `N` (≤ 5
 * tableInputs per sheet on canonical models).
 */
export interface TableInputRegion {
  name: string;
  rowMin: number;
  rowMax: number;
  colMin: number;
  colMax: number;
}

export function getTableInputRegions(
  tableInputs: unknown[],
  sheetName: string
): TableInputRegion[] {
  const regions: TableInputRegion[] = [];
  for (const raw of tableInputs) {
    const t = raw as Partial<TableInputDefinition>;
    if (!t || typeof t.sheet !== 'string' || t.sheet !== sheetName) continue;
    if (
      typeof t.topLeftCell !== 'string' ||
      typeof t.bottomRightCell !== 'string'
    )
      continue;
    const topLeft = parseA1(t.topLeftCell);
    const bottomRight = parseA1(t.bottomRightCell);
    if (!topLeft || !bottomRight) continue;
    regions.push({
      name: typeof t.name === 'string' ? t.name : 'unnamed',
      rowMin: topLeft.row,
      rowMax: bottomRight.row,
      colMin: topLeft.col,
      colMax: bottomRight.col,
    });
  }
  return regions;
}

/**
 * Returns true when `(row, col)` sits on the LEFT EDGE of any tableInput
 * region — used by the grid to apply the `border-l-2 border-l-accent/30`
 * bracket. Interior cells of the region don't get the bracket; the visual
 * indicator marks the region's left boundary only.
 */
export function isLeftEdgeOfTableRegion(
  row: number,
  col: number,
  regions: TableInputRegion[]
): boolean {
  for (const r of regions) {
    if (col === r.colMin && row >= r.rowMin && row <= r.rowMax) return true;
  }
  return false;
}

/**
 * Format a cell value for display.
 *
 * Dispatch chain:
 *   1. null/undefined → empty string
 *   2. error literal → preserved verbatim (rendered in `text-danger` by
 *      the caller via `getCellClassName({isError: true})`)
 *   3. boolean → TRUE/FALSE
 *   4. number with `cell.style.numberFormat` → `formatExcelNumber`
 *   5. number with `cell.namedRange` matching `schema.inputs[]` /
 *      `schema.outputs[]` `dataType` → per-type formatter
 *   6. number fallback → `value.toLocaleString('en-US')`
 *   7. string fallback → `String(value)`
 */
export function formatCellValue(
  value: unknown,
  cell: GridCell,
  schema: ModelViewSchemaSubset
): string {
  if (value === null || value === undefined) return '';
  // Label cells (model-view-fidelity Phase 4, 2026-04-27): `resolveValue`
  // returns `cell.text` for labels, so `value` here is the layout's raw
  // text. Skip the numeric / CellError / boolean pipeline — labels are
  // pure display strings. The CellError check below still catches the
  // (defensive) case where a label cell somehow holds an error value.
  if (cell.cellType === 'label' && typeof value === 'string') return value;
  // `#N/A` (lookup miss) renders as an em dash — semantically "value not
  // available yet, fill in the inputs that drive this lookup." Distinct
  // from the red-text error path below which is reserved for real workbook
  // bugs (#REF!/#VALUE!/#NUM!/#CYCLE!/etc.). Caller pairs this with the
  // `isMissing` className override for muted styling. (2026-05-04)
  if (isMissingValueError(value)) return '—';
  // CellError object form — unwrap to the literal BEFORE the string-error
  // check so the existing red-glyph styling kicks in via getCellClassName
  // (which calls isErrorValue → returns true → text-danger applied).
  // Added by model-view-fidelity Phase 1 (2026-04-26) — without this branch
  // the `String(value)` fallback at line ~end produces "[object Object]" for
  // every CellError that flows through the API to the grid.
  if (isCellErrorObject(value)) return value.value;
  if (typeof value === 'string' && isErrorValue(value)) return value;
  if (typeof value === 'boolean') return value ? 'TRUE' : 'FALSE';

  if (typeof value === 'number') {
    if (cell.style?.numberFormat) {
      return formatExcelNumber(value, cell.style.numberFormat);
    }
    if (cell.namedRange) {
      const def =
        (schema.inputs as Array<{ name: string; dataType?: string }>).find(
          (i) => i.name === cell.namedRange
        ) ??
        (schema.outputs as Array<{ name: string; dataType?: string }>).find(
          (o) => o.name === cell.namedRange
        );
      if (def?.dataType) {
        switch (def.dataType) {
          case 'percentage':
            return formatPercent(value, 2);
          case 'currency':
            return formatCurrency(value, 0);
          case 'currency_per_sf':
            return `${formatCurrency(value, 0)}/SF`;
          case 'currency_per_unit_year':
            return `${formatCurrency(value, 0)}/unit/yr`;
          case 'integer':
            return formatNumber(value, 0);
          case 'decimal':
            return formatNumber(value, 2);
        }
      }
    }
    return value.toLocaleString('en-US');
  }

  return String(value);
}

// ---------------------------------------------------------------------------
// F4 — formatCellValueFragment (model-view-excel-parity Phase 1, ADR-0001)
// ---------------------------------------------------------------------------

/**
 * F4 sibling of `formatCellValue` (Phase 1 of model-view-excel-parity, ADR-0001).
 * Returns the formatted value decomposed into prefix / body / suffix parts
 * so DataCell can render flexbox `justify-between` for column-aligned `$`
 * glyphs (Excel "Accounting" format parity). Mirrors `formatCellValue`'s
 * 7-branch dispatch chain (research § 4 block 10 verbatim).
 *
 * Currency branches return `{prefix: '$', body: <digits>, suffix?: <unit>}`.
 * Non-currency branches return `{body: <stringified-result>}`.
 *
 * Uses `Intl.NumberFormat.formatToParts()` (NOT string-slice
 * `formatCurrency(...).slice(1)`) to handle negatives + accounting parens
 * correctly (research § 11 R16).
 *
 * `formatCellValue` (string variant) UNCHANGED across all phases per ADR-0001.
 * `CellDetailPanel.tsx` + `PanelDashboard.tsx` + barrel `index.ts` consumers
 * STAY on string.
 */
export function formatCellValueFragment(
  value: unknown,
  cell: GridCell,
  schema: ModelViewSchemaSubset
): {
  prefix?: string;
  body: string;
  suffix?: string;
  /**
   * 2026-05-27 — set when the body is a synthesized placeholder (e.g. empty
   * `dataType: 'location'` input cells render "Set address" instead of an
   * unreadable blank). The renderer styles placeholder bodies in muted
   * italic so they read as a CTA, not as data. Empty bodies and real values
   * never carry this flag.
   */
  placeholder?: boolean;
} {
  if (value === null || value === undefined) {
    // 2026-05-27 — when an `input` cell whose schema InputDefinition has
    // `dataType: 'location'` is empty, render a discoverable placeholder
    // inside the cell. Default column widths in DealSummary make empty
    // single-cell location inputs look invisible; the placeholder gives
    // the user a clear "click here" affordance even at the narrow width.
    if (cell.cellType === 'input' && cell.namedRange) {
      const def = (
        schema.inputs as Array<{ name: string; dataType?: string }>
      ).find((i) => i.name === cell.namedRange);
      if (def?.dataType === 'location') {
        return { body: 'Set address', placeholder: true };
      }
    }
    return { body: '' };
  }
  if (cell.cellType === 'label' && typeof value === 'string')
    return { body: value };
  if (isMissingValueError(value)) return { body: '—' };
  if (isCellErrorObject(value)) return { body: value.value };
  if (typeof value === 'string' && isErrorValue(value)) return { body: value };
  if (typeof value === 'boolean') return { body: value ? 'TRUE' : 'FALSE' };

  if (typeof value === 'number') {
    if (cell.style?.numberFormat) {
      // Excel-format cells: route through formatExcelNumber for the body.
      // F-I1 (post-ship remediation, 2026-05-25): when the Excel pattern is
      // the Accounting format (`_("$"* ...` / `"$"#,##0...` etc), split out
      // the `$` glyph so DataCell can render flex justify-between column-
      // aligned `$` — restoring the F4 column-alignment outcome that the
      // pre-remediation code path silently dropped (both ref workbooks stamp
      // numberFormat on every currency cell, short-circuiting the
      // schema-dataType branch below).
      const body = formatExcelNumber(value, cell.style.numberFormat);
      const isAccountingFormat =
        /["$]\$/.test(cell.style.numberFormat) ||
        cell.style.numberFormat.includes('$');
      if (isAccountingFormat && body.startsWith('$')) {
        return { prefix: '$', body: body.slice(1).trimStart() };
      }
      // Negative-in-parens Accounting case: body starts with `($`. 2026-05-26:
      // user direction — for negative parenthesized values, keep `$` GLUED to
      // the open-paren (`($1,234)`) instead of splitting `prefix:'$'` to the
      // column-left edge. The split was the F-I1 (2026-05-25) design to align
      // `$` across rows, but visually creates a wide gap `$     (1,234)` that
      // reads as broken on narrow columns. Return the whole formatted string
      // in `body`; positive cells still get the prefix-split for alignment.
      if (isAccountingFormat && body.startsWith('($')) {
        return { body };
      }
      return { body };
    }
    if (cell.namedRange) {
      const def =
        (schema.inputs as Array<{ name: string; dataType?: string }>).find(
          (i) => i.name === cell.namedRange
        ) ??
        (schema.outputs as Array<{ name: string; dataType?: string }>).find(
          (o) => o.name === cell.namedRange
        );
      if (def?.dataType) {
        switch (def.dataType) {
          case 'percentage':
            return { body: formatPercent(value, 2) };
          case 'currency':
            // Use Intl.NumberFormat.formatToParts for correct negatives +
            // accounting parens (NOT formatCurrency(...).slice(1) — fragile
            // per research § 11 R16).
            return splitCurrencyParts(value, 0);
          case 'currency_per_sf': {
            const parts = splitCurrencyParts(value, 0);
            return { ...parts, suffix: '/SF' };
          }
          case 'currency_per_unit_year': {
            const parts = splitCurrencyParts(value, 0);
            return { ...parts, suffix: '/unit/yr' };
          }
          case 'integer':
            return { body: formatNumber(value, 0) };
          case 'decimal':
            return { body: formatNumber(value, 2) };
        }
      }
    }
    return { body: value.toLocaleString('en-US') };
  }

  return { body: String(value) };
}

/**
 * F4 helper — splits a currency-formatted number into prefix ($) + body (digits).
 * Uses `Intl.NumberFormat.formatToParts` per research § 11 R16 (correct on
 * negatives + accounting parens, unlike `.slice(1)`). Internal-only; not
 * barrel-exported.
 */
function splitCurrencyParts(
  value: number,
  decimals: number
): { prefix: string; body: string } {
  const parts = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).formatToParts(value);
  let prefix = '';
  let body = '';
  for (const part of parts) {
    if (part.type === 'currency') {
      prefix = part.value;
    } else {
      body += part.value;
    }
  }
  return { prefix, body };
}
